/**
 * SignedContractsTable
 * Lista de contratos firmados del usuario con botón "Ver PDF" inline.
 */

import { Eye, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { humanizeContractType } from '@/features/credit/hooks/useSignedContracts';
import type { SignedContractListItem } from '@/types/credit.types';

interface Props {
  contracts: SignedContractListItem[];
  isLoading: boolean;
  isError: boolean;
  viewingId: string | null;
  onView: (item: SignedContractListItem) => void;
  onRetry: () => void;
}

function formatCreatedAt(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function truncateHash(hash: string | undefined): string {
  if (!hash) return '-';
  return `${hash.slice(0, 10)}…${hash.slice(-6)}`;
}

export function SignedContractsTable({
  contracts,
  isLoading,
  isError,
  viewingId,
  onView,
  onRetry,
}: Props) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={`signed-contract-skeleton-${i.toString()}`}
            variant="rectangular"
            height={48}
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-sm text-muted-foreground">
        <span>Error al cargar los contratos firmados.</span>
        <Button onClick={onRetry} size="sm">
          <RefreshCw size={14} />
          Reintentar
        </Button>
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Este usuario no tiene contratos firmados todavía.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Documento</th>
            <th className="px-3 py-2 text-left font-medium">Folio</th>
            <th className="px-3 py-2 text-left font-medium">Versión</th>
            <th className="px-3 py-2 text-left font-medium">Hash</th>
            <th className="px-3 py-2 text-left font-medium">Firmado</th>
            <th className="px-3 py-2 text-right font-medium">Acción</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((c) => {
            const isOpening = viewingId === c.contract_id;
            return (
              <tr key={c.contract_id} className="border-t border-border">
                <td className="px-3 py-2 font-medium text-foreground">{humanizeContractType(c.contract_type)}</td>
                <td className="px-3 py-2 text-muted-foreground font-mono text-xs">{c.folio || '-'}</td>
                <td className="px-3 py-2">
                  <Badge variant="default">v{c.version || '01'}</Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground font-mono text-xs" title={c.file_hash}>
                  {truncateHash(c.file_hash)}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{formatCreatedAt(c.created_at)}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={isOpening}
                    onClick={() => onView(c)}
                  >
                    <Eye size={14} />
                    {isOpening ? 'Abriendo...' : 'Ver PDF'}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
